/**
 * WMS 표시 계층 전용 option/enum/단위 formatter.
 * API 값·코드·DB 스키마는 그대로 유지하고 관리자 화면에서만
 * 한국어 우선 표시와 코드 병기를 제공한다.
 */

export type WmsOptionKind =
  | 'item'
  | 'unit'
  | 'field'
  | 'location'
  | 'seed_lot'
  | 'crop_batch'
  | 'grade'
  | 'bucket';

type WmsValue = Record<string, any> | string | number | null | undefined;

const NAME_LABELS: Record<string, string> = {
  Tomato: '방울토마토', Lettuce: '상추', Basil: '바질', 'Tomato seed': '방울토마토 종자',
  Each: '개', Seed: '립·종자 낱알', Plant: '주·식물 한 포기', Pack: '팩', Bag: '봉', Box: '상자', Tray: '트레이',
  Gram: '그램', Kilogram: '킬로그램', 'Metric ton': '톤', 'Square meter': '제곱미터', Pyeong: '평', Are: '아르', Hectare: '헥타르',
  'Greenhouse A': '스마트온실 A동', 'Greenhouse B': '스마트온실 B동', 'Open field 1': '노지 1구역',
  'Seed storage': '종자창고', 'Harvest staging': '수확대기장', 'Cold storage': '저온창고', Shipping: '출고장', Waste: '폐기장',
  Premium: '특품', 'Grade A': 'A등급', Normal: '일반', Available: '가용', Reserved: '예약', 'Quality hold': '품질보류', Production: '생산', Sale: '판매',
};

const ENUM_LABELS: Record<string, string> = {
  count: '개수형', weight_g: '중량형·그램 기준', area_m2: '면적형·제곱미터 기준', seed: '종자', crop: '작물', product: '상품', material: '자재',
  farm: '농장', greenhouse: '온실', zone: '구역', open_field: '노지', field: '필드', warehouse: '창고', cold_storage: '저온창고', packing: '포장장', other: '기타',
  available: '가용', reserved: '예약', qc_hold: '품질보류', production: '생산', sale: '판매', sowing: '파종', germination: '발아', transplant: '정식', growth: '생육', harvest_ready: '수확준비', closed: '종료',
  draft: '초안', posted: '확정', cancelled: '취소', held: '예약중', released: '해제', fulfilled: '출고완료', expired: '만료', receipt: '입고', transfer_out: '이관출고', transfer_in: '이관입고', issue: '일반출고', waste: '폐기', reversal: '취소반전', open: '미마감', reopened: '재개방', confirmed: '확정', planned: '예정', active: '사용 중', inactive: '미사용',
};

function record(value: WmsValue): Record<string, any> {
  return value && typeof value === 'object' ? value : {};
}

function rawName(value: WmsValue): string {
  const item = record(value);
  return String(item.display_name ?? item.name ?? item.title ?? '').trim();
}

function rawCode(value: WmsValue): string {
  return String(record(value).code ?? '').trim();
}

function mappedName(value: string): string {
  if (!value) return '';
  if (/[\u3131-\uD79D]/u.test(value)) return value;
  return NAME_LABELS[value] ?? value;
}

function withCode(label: string, code: string): string {
  if (label && code) return `${label} (${code})`;
  if (label) return label;
  if (code) return `명칭 미등록 (${code})`;
  return '명칭 미등록';
}

export function formatItemOption(value: WmsValue): string { return withCode(mappedName(rawName(value)), rawCode(value)); }
export function formatUnitOption(value: WmsValue): string { return withCode(mappedName(rawName(value)), rawCode(value)); }
export function formatFieldOption(value: WmsValue): string { return withCode(mappedName(rawName(value)), rawCode(value)); }
export function formatLocationOption(value: WmsValue): string { return withCode(mappedName(rawName(value)), rawCode(value)); }
export function formatGradeOption(value: WmsValue): string { return withCode(mappedName(rawName(value)), rawCode(value)); }
export function formatBucketOption(value: WmsValue): string { return withCode(mappedName(rawName(value)), rawCode(value)); }

export function formatSeedLotOption(value: WmsValue): string {
  const item = record(value);
  const code = String(item.lot_no ?? item.code ?? '').trim();
  const itemLabel = item.item ? formatItemOption(item.item) : '';
  return itemLabel ? `${itemLabel} · 종자로트 (${code || '명칭 미등록'})` : code ? `종자로트 (${code})` : '종자로트 (명칭 미등록)';
}

export function formatCropBatchOption(value: WmsValue): string {
  const item = record(value);
  const code = String(item.batch_no ?? item.code ?? '').trim();
  const itemLabel = item.item ? formatItemOption(item.item) : mappedName(String(item.item_name ?? '').trim());
  const fieldLabel = item.field ? formatFieldOption(item.field) : mappedName(String(item.field_name ?? '').trim());
  const context = [itemLabel, fieldLabel].filter(Boolean).join(' · ');
  return context ? `${context} (${code || '명칭 미등록'})` : code ? `재배배치 (${code})` : '재배배치 (명칭 미등록)';
}

export function formatWmsOption(value: WmsValue, kind: WmsOptionKind): string {
  switch (kind) {
    case 'item': return formatItemOption(value);
    case 'unit': return formatUnitOption(value);
    case 'field': return formatFieldOption(value);
    case 'location': return formatLocationOption(value);
    case 'seed_lot': return formatSeedLotOption(value);
    case 'crop_batch': return formatCropBatchOption(value);
    case 'grade': return formatGradeOption(value);
    case 'bucket': return formatBucketOption(value);
    default: return withCode(mappedName(rawName(value)), rawCode(value));
  }
}

export function formatWmsEnum(value: WmsValue): string {
  const raw = typeof value === 'string' || typeof value === 'number' ? String(value) : rawCode(value) || rawName(value);
  if (!raw) return '명칭 미등록';
  return `${ENUM_LABELS[raw] ?? raw} (${raw})`;
}

export function formatWmsQuantity(value: WmsValue, unit?: WmsValue): string {
  const quantity = value == null || value === '' ? '0' : String(value);
  return unit ? `${quantity} ${formatUnitOption(unit)}` : quantity;
}
